import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export function TrainingAcks() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get("/compliance/acks").then((r) => setData(r.data)).catch(() => setData({ staff: [], acked: 0, total: 0, version: 1 })); }, []);
  useEffect(() => { load(); }, [load]);

  const reset = async () => {
    if (!window.confirm("Start a new training cycle? Every staff member will be asked to acknowledge HIPAA training again.")) return;
    setBusy(true);
    try { await api.post("/compliance/training/reset"); toast.success("New training cycle started"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const pct = data.total ? Math.round((data.acked / data.total) * 100) : 100;
  return (
    <div className="space-y-4 max-w-3xl" data-testid="training-acks">
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-slate-900">Workforce training acknowledgements · cycle {data.version}</div>
          <div className="text-sm text-slate-600 mt-0.5"><b data-testid="training-acked-count">{data.acked}</b> of {data.total} staff have acknowledged. Staff see a banner until they do.</div>
          <div className="mt-2 h-2 w-64 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
        </div>
        <button onClick={reset} disabled={busy} data-testid="training-reset-btn" className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} New training cycle
        </button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50"><th className="px-5 py-2.5">Name</th><th className="px-5 py-2.5">Role</th><th className="px-5 py-2.5">Status</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.staff.map((u) => (
              <tr key={u.id} data-testid={`training-row-${u.id}`}>
                <td className="px-5 py-3"><div className="font-medium text-slate-900">{u.name}</div><div className="text-xs text-slate-500">{u.email || "PIN-only"}</div></td>
                <td className="px-5 py-3 capitalize text-slate-700">{u.role.replace("_", " ")}</td>
                <td className="px-5 py-3">
                  {u.acked ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {new Date(u.acked_at).toLocaleDateString()}</span>
                           : <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-4 w-4" /> Pending</span>}
                </td>
              </tr>
            ))}
            {data.staff.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-slate-500">No staff accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
